import {
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  CaseEventType,
  CaseExtendedModel,
  CaseModel,
  CaseStatusEnum,
  CaseTypeEnum,
} from 'api/model';
import {
  useCaseAddOrUpdateCommentHook,
  useCaseCreateCaseHook,
  useCaseGetCaseOverlapsHook,
  useCaseGetCasesByRideOrderId,
  useCaseUpdateCaseHook,
} from 'api/case/case';
import useCaseAssignmentForms, {
  // assignmentFormToAssignment,
  assignmentFormToAssignments,
} from './useCaseAssignmentForms';
import useCaseDataForm, {
  CaseDataField,
  caseDataFormToCaseModel,
  getClearedVehicleDataFormValues,
  initializeCaseDataForm,
  normalizeRegistrationNumber,
} from './useCaseDataForm';
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import { useAssignmentCalculateFixedAssignmentTimeHook } from 'api/assignment/assignment';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import useBreakOnTopPlanningSetting from 'hooks/useBreakOnTopPlanningSetting';
import useUsers from 'contexts/basicData/useUsers';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';
import { useCopyCaseValue } from 'contexts/basicData/useCopyCase';
import useModalStack from 'contexts/modal/useModalStack';
import ConfirmModal from 'components/Modal/ConfirmModal';
import ActionModal from 'components/Modal/ActionModal';
import ZakraHubReasonModal from 'components/DetailedCase/ZakraHubReasonModal';
import { isSelfTransportationAssignment } from './types';
import {
  candidateBookedToDateChanged,
  caseMatchesSameDayDirektbokCriteria,
  caseNeedsTLDirectBookingAnswer,
  isNeverDirectBooking,
  requiresExplicitDirectBookingFlag,
} from 'utils/sameDayDirektbok';

const CaseDataFormContext = createContext<
  ReturnType<typeof useCaseDataForm> | undefined
>(undefined);

const CaseAssignmentFormContext = createContext<
  ReturnType<typeof useCaseAssignmentForms> | undefined
>(undefined);

interface Controls {
  /** Elbil-flaggan på det sparade ärendet — för att se om den ändrats i formuläret. */
  savedIsElectricCase: boolean;
  save: () => Promise<[caseId: number | null, error: null | unknown]>;
  startEdit(c: CaseExtendedModel | null, caseType?: CaseTypeEnum): void;
  checkForOverlaps(): Promise<CaseModel[]>;
  checkForCommentsNeeded: () => Record<number, string[]>;
  checkIfConnectedTradeInWillAutoUpdate(): Promise<boolean | null>;
  cancelEdit(): void;
  copy(): void;
  editMode: boolean;
  getNewOrUpdatedCase: (from: string) => {
    // Exposed for the comments needed modal form, in order to present updated case info
    caseModel: CaseModel;
  };
  hasErrors: boolean;
  createCaseStatus: RequestStatus;
  updateCaseStatus: RequestStatus;
  sendCommentToFieldTester: (
    caseModel: number,
    comment: string,
    assignmentId: number
  ) => Promise<unknown>;
  isNewCase: boolean;
}
const ControlsContext = createContext<Controls | undefined>(undefined);

const DetailedCaseFormProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const modalStack = useModalStack();
  const { zipCodeAreas } = useTranslations();
  const { companiesById } = useCompanies();
  const usersById = useUsers();
  // Keep the "break on top in planning" rollout date (utils/breakOnTopPlanning) synced with the global setting
  // for the whole case-detail tree (form load/save + EstimatedAssignmentTimeValue display).
  useBreakOnTopPlanningSetting();
  const [controlsState, setControlsState] = useState({
    /** if null, create a new case */
    caseBeingEdited: null as CaseExtendedModel | null,
    editMode: false,
  });
  const caseDataForm = useCaseDataForm();
  const caseAssignmentForms = useCaseAssignmentForms();
  const caseCopy = useCopyCaseValue();

  // Regnr som kopierades in (normaliserat), eller null när formuläret inte är en
  // kopia. Sätts bara i handleCopy så att rensningen av fordonsdata aldrig triggas
  // vid vanlig nyskapning eller redigering av ett befintligt uppdrag.
  const [copyBaselineRegnr, setCopyBaselineRegnr] = useState<string | null>(
    null
  );

  const createCase = useCaseCreateCaseHook();
  const updateCase = useCaseUpdateCaseHook();
  const getCaseOverlaps = useCaseGetCaseOverlapsHook();
  const addOrUpdateComment = useCaseAddOrUpdateCommentHook();
  const calculateFixedTime = useAssignmentCalculateFixedAssignmentTimeHook();

  const { run: createCaseCall, status: createCaseStatus } = useApiCallFn(
    (caseModel: CaseModel) => createCase(caseModel)
  );
  const { run: updateCaseCall, status: updateCaseStatus } = useApiCallFn(
    (caseModel: CaseModel) =>
      updateCase(caseModel, {
        eventType: CaseEventType.CaseEdited,
        eventMessage: 'Ärende redigerades',
      })
      );

  const { mutateAsync: getCasesByRideOrderId } = useCaseGetCasesByRideOrderId();

  const { run: checkForOverlapsCall } = useApiCallFn((caseModel: CaseModel) =>
    getCaseOverlaps(caseModel)
  );

  const { run: sendCommentToFieldTester } = useApiCallFn(
    (caseModelID: number, comment: string, assignmentId: number) =>
      addOrUpdateComment({
        caseId: caseModelID,
        message: comment,
        internalComment: false,
        assignmentId,
        readByUser: false,
      })
  );

  const caseDataFormRef = useRef(caseDataForm);
  caseDataFormRef.current = caseDataForm;
  const caseAssignmentFormsRef = useRef(caseAssignmentForms);
  caseAssignmentFormsRef.current = caseAssignmentForms;

  const handleStartEdit = useCallback(
    (caseExtended: CaseExtendedModel | null, caseType?: CaseTypeEnum) => {
      caseAssignmentFormsRef.current.cancelAllEdits();

      // Vanlig nyskapning/redigering — ingen kopia, så fordonsdata ska aldrig rensas.
      setCopyBaselineRegnr(null);

      // Initialize case form
      caseDataFormRef.current.setMultiple(
        initializeCaseDataForm(caseExtended?.case, caseType)
      );

      if (!caseExtended) {
        // start a new assignment if the case is also new
        caseAssignmentFormsRef.current.startEdit(
          null,
          caseType ?? CaseTypeEnum.Purchase,
          caseType === CaseTypeEnum.Purchase || caseType === CaseTypeEnum.Auction
            ? AssignmentTypeEnum.WithTest
            : undefined
        );
      } else {
        // Open all assignments for edit
        caseExtended.case.assignmentList.forEach((assignment) =>
          caseAssignmentFormsRef.current.startEdit(
            assignment,
            caseType ?? CaseTypeEnum.Purchase
          )
        );
      }

      setControlsState({
        caseBeingEdited: caseExtended,
        editMode: true,
      });
    },
    []
  );

  const handleCancelEdit = useCallback(() => {
    caseAssignmentForms.cancelAllEdits();
    setControlsState({ editMode: false, caseBeingEdited: null });
    setCopyBaselineRegnr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseAssignmentForms.cancelAllEdits]);

  /**
   * Returns the current version of the case as edited by the user
   */
  const getNewOrUpdatedCase = useCallback((): {
    caseModel: CaseModel;
    fieldsInNeedOfExtraInfo: Record<number, string[]>;
  } => {
    const newOrUpdatedCase = caseDataFormToCaseModel(
      controlsState.caseBeingEdited?.case,
      caseDataFormRef.current.state,
      zipCodeAreas
    );
    const fieldsInNeedOfExtraInfo: Record<number, string[]> = {};
    // Update assignments inside the case
    Object.entries(caseAssignmentFormsRef.current.state).forEach(
      ([assignmentIdStr, assignmentForm]) => {
        const assignmentId = Number(assignmentIdStr);
        const caseAssignmentIndex = newOrUpdatedCase.assignmentList.findIndex(
          (a) => a.assignmentID === assignmentId
        );
        const caseAssignment = newOrUpdatedCase.assignmentList[
          caseAssignmentIndex
        ] as AssignmentModel | undefined;

        // const updatedOrNewAssignment = assignmentFormToAssignment(
        //   caseAssignment,
        //   assignmentForm,
        //   usersById,
        //   zipCodeAreas
        // );

        const updatedOrNewAssignments = assignmentFormToAssignments(
          caseAssignment,
          assignmentForm,
          usersById,
          zipCodeAreas,
          newOrUpdatedCase.caseTypeID
        );

        // for each edited assignment that is a new one (id === -1) we need to add it to the case
        updatedOrNewAssignments.forEach((editedAssignment) => {
          const theAssignment = editedAssignment;

          // 1) If this case has become a Placeholder, force the right assignmentType
          if (newOrUpdatedCase.caseTypeID === CaseTypeEnum.Placeholder) {
            theAssignment.assignmentTypeID = AssignmentTypeEnum.CarPlaceholder;
          }

          // 2) NEW (local) assignments: ID === -1
          if (theAssignment.assignmentID === -1) {
            const existingLocalIndex =
              newOrUpdatedCase.assignmentList.findIndex(
                (a) => a.localAssignmentID === theAssignment.localAssignmentID
              );
            if (existingLocalIndex > -1) {
              // replace the (already-pushed) one
              newOrUpdatedCase.assignmentList[existingLocalIndex] =
                theAssignment;
            } else {
              // still new, so push it once
              newOrUpdatedCase.assignmentList.push(theAssignment);
            }

            // 3) EXISTING assignments: ID ≥ 0
          } else {
            const existingIndex = newOrUpdatedCase.assignmentList.findIndex(
              (a) => a.assignmentID === theAssignment.assignmentID
            );
            if (existingIndex > -1) {
              newOrUpdatedCase.assignmentList[existingIndex] = theAssignment;
            } else {
              // (edge-case: maybe it really is new)
              newOrUpdatedCase.assignmentList.push(theAssignment);
            }

            // 4) finally, check for time changes and collect fields
            const original = caseAssignment; // the “before” snapshot
            if (
              theAssignment.startTime?.getTime() !==
              original?.startTime?.getTime()
            ) {
              fieldsInNeedOfExtraInfo[theAssignment.assignmentID] ??= [];
              fieldsInNeedOfExtraInfo[theAssignment.assignmentID].push(
                'Starttid'
              );
            }
            if (
              theAssignment.endTime?.getTime() !== original?.endTime?.getTime()
            ) {
              fieldsInNeedOfExtraInfo[theAssignment.assignmentID] ??= [];
              fieldsInNeedOfExtraInfo[theAssignment.assignmentID].push(
                'Sluttid'
              );
            }
          }
        });
      }
    );

    return {
      caseModel: newOrUpdatedCase,
      fieldsInNeedOfExtraInfo,
    };
  }, [controlsState.caseBeingEdited?.case, usersById, zipCodeAreas]);

  const verifyBookedToDateAndProceed = useCallback(
    (newOrUpdatedCase: CaseModel, callback: () => Promise<void>) => {
      return new Promise<boolean>((resolve) => {
        const bookedToTooFarInThePastOrFuture =
          newOrUpdatedCase.assignmentList.some(
            (assignment) =>
              assignment.bookedTo <
                new Date(Date.now() - 60 * 24 * 60 * 60 * 1000) ||
              assignment.bookedTo >
                new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)
          );

        const runCallback = async () => {
          try {
            await callback();
            resolve(true);
          } catch (e) {
            // If callback throws, resolve false to indicate it didn't complete successfully
            resolve(false);
          }
        };

        if (bookedToTooFarInThePastOrFuture) {
          // Show warning modal and resolve when user confirms or cancels.
          modalStack.push(
            <ConfirmModal
              title="Bokad tid är långt bort"
              content="Den bokade tiden ligger mer än 60 dagar i framtiden eller i det förflutna."
              onConfirm={async () => {
                modalStack.pop();
                // run and resolve when done
                await runCallback();
              }}
              onCancel={() => {
                modalStack.pop();
                resolve(false);
              }}
            />
          );
        } else {
          // No modal required; run callback and resolve after it completes.
          runCallback().catch(() => {});
        }
      });
    },
    [modalStack]
  );

  /**
   * If the original case is Closed but the (new) assignments are not all Approved or Discarded,
   * show a modal asking to reopen the case. Runs `callback` only when proceeding.
   */
  const verifyClosedCaseAssignmentsAndProceed = useCallback(
    (newOrUpdatedCase: CaseModel) => {
      return new Promise<
        'save_and_reopen' | 'save_and_leave_casestatus' | 'cancel'
      >((resolve) => {
        // If there's no original case or it's not closed, proceed immediately
        if (newOrUpdatedCase.caseStatusID !== CaseStatusEnum.Closed) {
          resolve('save_and_leave_casestatus');
          return;
        }

        const hasNonApprovedOrDiscarded = newOrUpdatedCase.assignmentList.some(
          (a) =>
            a.assignmentStatusID !== AssignmentStatusEnum.Approved &&
            a.assignmentStatusID !== AssignmentStatusEnum.Discarded
        );

        if (hasNonApprovedOrDiscarded) {
          modalStack.push(
            <ActionModal
              title="Obs: Ärendet är stängt"
              onClose={() => {
                modalStack.pop();
                resolve('cancel');
              }}
              actions={[
                {
                  label: 'Spara och öppna ärendet igen',
                  onClick: () => {
                    modalStack.pop();
                    resolve('save_and_reopen');
                  },
                  color: 'primary',
                },
                {
                  label: 'Spara och lämna ärendet stängt',
                  onClick: () => {
                    modalStack.pop();
                    resolve('save_and_leave_casestatus');
                  },
                  color: 'secondary',
                },
                {
                  label: 'Avbryt',
                  onClick: () => {
                    modalStack.pop();
                    resolve('cancel');
                  },
                  color: 'secondary',
                },
              ]}
            >
              <span>
                Obs: Ärendet är stängt och redo för fakturering, men ett eller
                flera uppdrag är inte godkända eller borttagna. Vill du öppna
                ärendet igen?
              </span>
            </ActionModal>
          );
        } else {
          resolve('save_and_leave_casestatus');
        }
      });
    },
    [modalStack]
  );

  /**
   * Sump, Samåkning and Annons cases are never auto-classified as direktbok by the same-day
   * snurra — the TL has to explicitly say so when creating the case. If the case matches the
   * criteria (BookedTo on the candidate assignment is the same day the case was created) and
   * the TL hasn't answered yet, prompt them with Ja/Nej before saving. The answer is stored on
   * the case (CaseModel.isDirectBooking).
   *
   * Resolves to true when the save should proceed, false if the user cancelled.
   */
  const verifyDirectBookingAndProceed = useCallback(
    (newOrUpdatedCase: CaseModel): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (!caseNeedsTLDirectBookingAnswer(newOrUpdatedCase)) {
          resolve(true);
          return;
        }

        modalStack.push(
          <ActionModal
            title="Är detta direktbok?"
            onClose={() => {
              modalStack.pop();
              resolve(false);
            }}
            actions={[
              {
                label: 'Ja, fakturera direktbok',
                onClick: () => {
                  modalStack.pop();
                  // eslint-disable-next-line no-param-reassign
                  newOrUpdatedCase.isDirectBooking = true;
                  resolve(true);
                },
                color: 'primary',
              },
              {
                label: 'Nej',
                onClick: () => {
                  modalStack.pop();
                  // eslint-disable-next-line no-param-reassign
                  newOrUpdatedCase.isDirectBooking = false;
                  resolve(true);
                },
                color: 'secondary',
              },
              {
                label: 'Avbryt',
                onClick: () => {
                  modalStack.pop();
                  resolve(false);
                },
                color: 'secondary',
              },
            ]}
          >
            <span>
              Uppdraget är bokat till samma dag som ärendet skapades. Ska
              ärendet faktureras som direktbokning?
            </span>
          </ActionModal>
        );
      });
    },
    [modalStack]
  );

  /**
   * Edit-time guard: if a BookedTo edit moves the candidate across the same-day-as-created
   * boundary, the persisted IsDirectBooking flag can drift out of sync with the snurra's
   * actual eligibility (the SQL pre-filter only includes assignments where
   * BookedTo.Date == Case.Created.Date). Prompt the TL when the boundary is crossed and the
   * flag is inconsistent with the new criteria-match state.
   *
   * Resolves true when save should proceed, false on Avbryt.
   */
  const verifyDirectBookingFlagAfterEdit = useCallback(
    (
      originalCase: CaseModel,
      newOrUpdatedCase: CaseModel
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (isNeverDirectBooking(newOrUpdatedCase)) {
          resolve(true);
          return;
        }
        if (!requiresExplicitDirectBookingFlag(newOrUpdatedCase)) {
          resolve(true);
          return;
        }

        // Only the candidate's BookedTo date moving matters — if the "main" assignment's
        // BookedTo calendar day is unchanged, the TL didn't shift the booking and shouldn't be
        // prompted (anything else they edited can't have crossed the same-day boundary).
        if (!candidateBookedToDateChanged(originalCase, newOrUpdatedCase)) {
          resolve(true);
          return;
        }

        const wasMatching = caseMatchesSameDayDirektbokCriteria(originalCase);
        const nowMatching =
          caseMatchesSameDayDirektbokCriteria(newOrUpdatedCase);

        if (wasMatching === nowMatching) {
          resolve(true);
          return;
        }

        const shouldOfferEnable =
          nowMatching && newOrUpdatedCase.isDirectBooking !== true;
        const shouldOfferDisable =
          !nowMatching && newOrUpdatedCase.isDirectBooking === true;

        if (!shouldOfferEnable && !shouldOfferDisable) {
          resolve(true);
          return;
        }

        const title = shouldOfferEnable
          ? 'Ska direktbok-flaggan sättas?'
          : 'Ska direktbok-flaggan tas bort?';
        const body = shouldOfferEnable
          ? 'Uppdraget är nu bokat samma dag som ärendet skapades. Ska ärendet faktureras som direktbokning?'
          : 'Uppdraget är inte längre bokat samma dag som ärendet skapades. Ska direktbok-flaggan tas bort?';
        const yesLabel = shouldOfferEnable
          ? 'Ja, sätt direktbok'
          : 'Ja, ta bort direktbok';
        const yesValue = shouldOfferEnable;

        modalStack.push(
          <ActionModal
            title={title}
            onClose={() => {
              modalStack.pop();
              resolve(false);
            }}
            actions={[
              {
                label: yesLabel,
                onClick: () => {
                  modalStack.pop();
                  // eslint-disable-next-line no-param-reassign
                  newOrUpdatedCase.isDirectBooking = yesValue;
                  resolve(true);
                },
                color: 'primary',
              },
              {
                label: 'Nej, behåll nuvarande',
                onClick: () => {
                  modalStack.pop();
                  resolve(true);
                },
                color: 'secondary',
              },
              {
                label: 'Avbryt',
                onClick: () => {
                  modalStack.pop();
                  resolve(false);
                },
                color: 'secondary',
              },
            ]}
          >
            <span>{body}</span>
          </ActionModal>
        );
      });
    },
    [modalStack]
  );

  /**
   * Zäkra-hubbar är Zäkras egna anläggningar — ett ärende som har en hub som företag kan inte
   * faktureras. Den som sätter hubben som företag måste därför förklara varför. Förklaringen
   * följer med sparningen som CaseModel.zakraHubReason och skrivs som en kommentar i ärendets
   * händelselogg; backend gör samma kontroll och avvisar sparningen om förklaringen saknas.
   *
   * Frågan ställs bara när sparningen FÖR IN hubben: ett nytt ärende med en hub som företag,
   * eller ett befintligt ärende vars företag byts från ett icke-hub-företag till en hub. Ett
   * ärende som redan ligger på en hub — eller som byter mellan två hubbar — frågas inte igen.
   *
   * Resolves to true when the save should proceed, false if the user cancelled.
   */
  const verifyZakraHubReasonAndProceed = useCallback(
    (
      newOrUpdatedCase: CaseModel,
      previousCompanyId: number | undefined
    ): Promise<boolean> => {
      const company = newOrUpdatedCase.companyID
        ? companiesById.get(newOrUpdatedCase.companyID)
        : undefined;
      const previousWasHub =
        previousCompanyId !== undefined &&
        companiesById.get(previousCompanyId)?.isZakraHub === true;

      if (company?.isZakraHub !== true || previousWasHub) {
        return Promise.resolve(true);
      }

      return new Promise<boolean>((resolve) => {
        modalStack.push(
          <ZakraHubReasonModal
            companyName={company.name}
            onConfirm={(reason) => {
              modalStack.pop();
              // eslint-disable-next-line no-param-reassign
              newOrUpdatedCase.zakraHubReason = reason;
              resolve(true);
            }}
            onClose={() => {
              modalStack.pop();
              resolve(false);
            }}
          />
        );
      });
    },
    [companiesById, modalStack]
  );

  /**
   * Fast tid styrs helt av adresserna: ändras en adress räknar backend om fältet vid spar
   * (CaseLogic.UpdateCaseAssignment). Finns en fast tid mellan de nya adresserna sätts den,
   * finns ingen rensas fältet — även när värdet var manuellt inmatat. Det är lätt att missa
   * mitt i en adressändring, så tala om vad som händer innan ärendet sparas.
   *
   * Vi frågar samma endpoint som backend räknar med istället för att lita på formulärets
   * fält, så att texten stämmer med vad som faktiskt sparas även om den löpande
   * uträkningen i useHandleChangedAddresses inte hunnit fram eller fallerat.
   *
   * Resolves true when save should proceed, false on Avbryt.
   */
  const verifyFixedAssignmentTimeAndProceed = useCallback(
    async (newOrUpdatedCase: CaseModel): Promise<boolean> => {
      const originalAssignments =
        controlsState.caseBeingEdited?.case.assignmentList ?? [];

      // Elbilsärenden har en egen spärr i backend (prislistan sätter aldrig fast tid, oavsett
      // rutt, och bara Efterkontroll/SuperAdmin sätter den för hand). Den hör inte ihop med
      // prislistans omräkning och varnas inte för här. Samma sak för samåkningar (egen spärr i
      // backend, samma regel som elbil).
      if (
        originalAssignments.length === 0 ||
        newOrUpdatedCase.isElectric ||
        newOrUpdatedCase.caseTypeID === CaseTypeEnum.Samakning
      ) {
        return true;
      }

      const trimmed = (value?: string) => (value ?? '').trim();

      // Befintliga uppdrag som har en fast tid att förlora och vars adresser ändrats.
      // Samma undantag som backend: Godkända och avbokade uppdrag räknas aldrig om.
      const candidates = newOrUpdatedCase.assignmentList
        .map((assignment) => ({
          assignment,
          original: originalAssignments.find(
            (a) => a.assignmentID === assignment.assignmentID
          ),
        }))
        .filter(
          ({ assignment, original }) =>
            !!original &&
            original.fixedAssignmentTime != null &&
            // Persontransport har en egen spärr i backend, precis som elbil ovan.
            !isSelfTransportationAssignment(assignment.assignmentTypeID) &&
            assignment.assignmentStatusID !== AssignmentStatusEnum.Approved &&
            assignment.assignmentCancelTypeID == null &&
            (trimmed(original.fromAddress) !==
              trimmed(assignment.fromAddress) ||
              trimmed(original.fromZip) !== trimmed(assignment.fromZip) ||
              trimmed(original.fromCity) !== trimmed(assignment.fromCity) ||
              trimmed(original.toAddress) !== trimmed(assignment.toAddress) ||
              trimmed(original.toZip) !== trimmed(assignment.toZip) ||
              trimmed(original.toCity) !== trimmed(assignment.toCity))
        );

      if (candidates.length === 0) {
        return true;
      }

      const affected = (
        await Promise.all(
          candidates.map(async ({ assignment, original }) => {
            const previousFixedTime = original?.fixedAssignmentTime ?? null;
            try {
              const result = await calculateFixedTime({
                fromName: assignment.fromName,
                fromAddress: assignment.fromAddress,
                fromCity: assignment.fromCity,
                fromZip: assignment.fromZip,
                toName: assignment.toName,
                toAddress: assignment.toAddress,
                toCity: assignment.toCity,
                toZip: assignment.toZip,
              });
              // bothCompaniesMatched=false ⇒ inget företagspar ⇒ ingen fast tid mellan
              // adresserna, samma utfall som ett matchande par utan prisrad.
              const nextFixedTime = result.bothCompaniesMatched
                ? (result.fixedTimeInMilliseconds ?? null)
                : null;

              if (nextFixedTime === previousFixedTime) {
                return null;
              }

              const previousText = getHoursAndMinutesFromMillisecondsString(
                previousFixedTime ?? 0
              );

              return {
                assignmentID: assignment.assignmentID,
                text:
                  nextFixedTime === null
                    ? `Uppdrag ${assignment.assignmentID}: fast tid ${previousText} tas bort — de nya adresserna har ingen fast tid.`
                    : `Uppdrag ${assignment.assignmentID}: fast tid ändras från ${previousText} till ${getHoursAndMinutesFromMillisecondsString(nextFixedTime)} — fast tid för de nya adresserna.`,
              };
            } catch {
              // Kan vi inte räkna om ska det inte blockera sparningen — backend räknar
              // ändå om, och en utebliven varning är bättre än ett ospårbart stopp.
              return null;
            }
          })
        )
      ).filter((row): row is { assignmentID: number; text: string } => !!row);

      if (affected.length === 0) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        modalStack.push(
          <ConfirmModal
            title="Fast tid påverkas av adressändringen"
            confirmText="Spara"
            cancelText="Avbryt"
            onConfirm={() => {
              modalStack.pop();
              resolve(true);
            }}
            onCancel={() => {
              modalStack.pop();
              resolve(false);
            }}
          >
            <span>
              Adresserna har ändrats. Fast tid räknas om efter de nya adresserna
              när ärendet sparas:
            </span>
            <ul>
              {affected.map((row) => (
                <li key={row.assignmentID}>{row.text}</li>
              ))}
            </ul>
          </ConfirmModal>
        );
      });
    },
    [calculateFixedTime, controlsState.caseBeingEdited?.case, modalStack]
  );

  /**
   * Check for overlapping assignments
   */
  const checkForOverlaps = useCallback(async (): Promise<CaseModel[]> => {
    const { caseModel: newOrUpdatedCase } = getNewOrUpdatedCase();

    const [overlappingCases] = await checkForOverlapsCall(newOrUpdatedCase);
    return overlappingCases ?? [];
  }, [checkForOverlapsCall, getNewOrUpdatedCase]);

  /**
   * Check for comments needed
   */
  const checkForCommentsNeeded = useCallback(() => {
    const { fieldsInNeedOfExtraInfo } = getNewOrUpdatedCase();

    return fieldsInNeedOfExtraInfo;
  }, [getNewOrUpdatedCase]);

  /**
   * Saves the case
   */
  const handleSave = useCallback(async (): Promise<
    [caseId: number | null, error: null | unknown]
  > => {
    const { caseModel: newOrUpdatedCase } = getNewOrUpdatedCase();

    // Ensure variables are always initialized to avoid "used before being assigned"
    let caseId: number | null = null;
    let error: null | unknown = null;

    if (
      controlsState.caseBeingEdited &&
      controlsState.caseBeingEdited?.case.caseID !== -1
    ) {
      try {
        // await updateCaseCall.mutateAsync({
        //   data: newOrUpdatedCase,
        //   params: {
        //     eventType: CaseEventType.CaseEdited,
        //     eventMessage: 'Ärende redigerades',
        //   },
        // });

        // verify closed-case + assignments; this may reopen the case before updating
        // clone case so we don't mutate the object returned from getNewOrUpdatedCase
        const action =
          await verifyClosedCaseAssignmentsAndProceed(newOrUpdatedCase);

        if (action === 'cancel') {
          return [null, null];
        }

        // If original case was closed and user chose to reopen, ensure the cloned case reflects that
        if (
          newOrUpdatedCase.caseStatusID === CaseStatusEnum.Closed &&
          action === 'save_and_reopen'
        ) {
          // the confirm modal resolved true; reopen before updating
          newOrUpdatedCase.caseStatusID = CaseStatusEnum.Open;
          newOrUpdatedCase.closedDate = undefined; // reset closed date
        }

        // If the edit moved the candidate across the same-day-as-created boundary, ask the TL
        // whether to flip IsDirectBooking so the snurra's eligibility matches the persisted flag.
        const originalCase = controlsState.caseBeingEdited?.case;
        if (originalCase) {
          const flagOk = await verifyDirectBookingFlagAfterEdit(
            originalCase,
            newOrUpdatedCase
          );
          if (!flagOk) {
            return [null, null];
          }
        }

        // Sump/Samåkning/Annons + same-day-direktbok candidate without a TL answer → prompt now
        const direktbokOk =
          await verifyDirectBookingAndProceed(newOrUpdatedCase);
        if (!direktbokOk) {
          return [null, null];
        }

        // Byttes företaget till en Zäkra-hub? Då krävs en förklaring innan sparningen.
        const zakraHubOk = await verifyZakraHubReasonAndProceed(
          newOrUpdatedCase,
          originalCase?.company?.companyID ?? originalCase?.companyID
        );
        if (!zakraHubOk) {
          return [null, null];
        }

        // Adressändring på ett uppdrag med fast tid → tala om att fast tid räknas om
        const fixedTimeOk =
          await verifyFixedAssignmentTimeAndProceed(newOrUpdatedCase);
        if (!fixedTimeOk) {
          return [null, null];
        }

        [, error] = await updateCaseCall(newOrUpdatedCase);
        caseId = controlsState.caseBeingEdited?.case.caseID ?? -1;
        caseAssignmentFormsRef.current.cancelAllEdits();
        setControlsState((s) => ({ ...s, editMode: false }));
      } catch (e) {
        caseId = null;
      }
    } else {
      // if the bookedTo of any assignment in the newOrUpdatedCase is more than 60 days ago or in the future, show a warning modal to allow the user to cancel the action
      try {
        // For new cases the case.created date isn't assigned by the server yet, so we use "now"
        // for the same-day-direktbok comparison — TL is creating the case right this moment.
        if (!newOrUpdatedCase.created) {
          newOrUpdatedCase.created = new Date();
        }
        const direktbokOk =
          await verifyDirectBookingAndProceed(newOrUpdatedCase);
        if (!direktbokOk) {
          return [null, null];
        }

        // Nytt ärende direkt på en Zäkra-hub kräver också en förklaring.
        const zakraHubOk = await verifyZakraHubReasonAndProceed(
          newOrUpdatedCase,
          undefined
        );
        if (!zakraHubOk) {
          return [null, null];
        }

        const result = await verifyBookedToDateAndProceed(
          newOrUpdatedCase,
          async () => {
            [caseId, error] = await createCaseCall(newOrUpdatedCase);
          }
        );
        if (!error && result) {
          setControlsState((s) => ({ ...s, editMode: false }));
          caseAssignmentFormsRef.current.cancelAllEdits();
        }
      } catch (exception) {
        // console.error('Error verifying booked date:', exception);
        // don't do anything
      }
    }

    return [caseId, error];
  }, [
    getNewOrUpdatedCase,
    controlsState.caseBeingEdited,
    updateCaseCall,
    verifyBookedToDateAndProceed,
    createCaseCall,
    verifyClosedCaseAssignmentsAndProceed,
    verifyDirectBookingAndProceed,
    verifyDirectBookingFlagAfterEdit,
    verifyZakraHubReasonAndProceed,
    verifyFixedAssignmentTimeAndProceed,
  ]);

  const handleCopy = useCallback(() => {
    if (caseCopy) {
      caseAssignmentFormsRef.current.cancelAllEdits();

      // Initialize case form
      caseDataFormRef.current.setMultiple(
        initializeCaseDataForm(caseCopy, caseCopy.caseTypeID)
      );

      // Kom ihåg originalbilens regnr så att fordonsdatan kan rensas om admin
      // byter regnr (kopian ska då köras med en annan bil).
      setCopyBaselineRegnr(normalizeRegistrationNumber(caseCopy.registrationNumber));

      caseAssignmentFormsRef.current.startEdit(
        caseCopy.assignmentList[0],
        caseCopy.caseTypeID,
        caseCopy.caseTypeID === CaseTypeEnum.Purchase ||
        caseCopy.caseTypeID === CaseTypeEnum.Auction
          ? AssignmentTypeEnum.WithTest
          : undefined
      );

      setControlsState({
        caseBeingEdited: {
          case: caseCopy,
          events: [],
          expenses: [],
          linkedCases: [],
        } satisfies CaseExtendedModel,
        editMode: true,
      });
    }
  }, [caseCopy]);

  // När formuläret är en kopia (copyBaselineRegnr !== null) och admin byter regnr
  // till en annan bil rensas all fordonsdata/flaggor en gång, så att originalbilens
  // uppgifter inte följer med. Vi väntar tills regnr är "färdigskrivet" (>= 6 tecken)
  // för att inte rensa mitt i inmatningen, och nollställer baseline efteråt så att
  // data som sedan fylls i för den nya bilen inte rensas av en efterföljande
  // regnr-justering.
  const currentRegnr = caseDataForm.state[CaseDataField.registrationNumber];
  useEffect(() => {
    if (copyBaselineRegnr === null) {
      return;
    }
    const normalized = normalizeRegistrationNumber(currentRegnr);
    if (normalized.length >= 6 && normalized !== copyBaselineRegnr) {
      caseDataFormRef.current.setMultiple(getClearedVehicleDataFormValues());
      setCopyBaselineRegnr(null);
    }
  }, [currentRegnr, copyBaselineRegnr]);

  /**
   * Check for overlapping assignments
   */
  const checkIfConnectedTradeInWillAutoUpdate =
    useCallback(async (): Promise<boolean> => {
      const { caseModel: newOrUpdatedCase } = getNewOrUpdatedCase();
      // if the current case type is not HomeDelivery or has more than one assignment (makes it difficult to auto-assign),
      // or has no linked cases we don't need to check for connected trade in
      if (
        newOrUpdatedCase.caseTypeID !== CaseTypeEnum.HomeDelivery ||
        !newOrUpdatedCase.hasLinkedCases ||
        newOrUpdatedCase.assignmentList.length > 1
      ) {
        return false;
      }

      // if the current case type is HomeDelivery, we need to check for connected trade in
      const connectedCases = await getCasesByRideOrderId({
        params: {
          RideOrderId: newOrUpdatedCase.rideOrderID,
        },
      });

      const connectedTradeIn = connectedCases?.find(
        (c) => c.caseTypeID === CaseTypeEnum.TradeIn
      );

      // return true if the connected trade in has only one assignment, with a different assignee than the current case
      if (
        connectedTradeIn?.assignmentList.length === 1 &&
        connectedTradeIn.assignmentList[0].assignedTo?.userID !==
          newOrUpdatedCase.assignmentList[0].assignedTo?.userID
      ) {
        return true;
      }
      return false;
    }, [getCasesByRideOrderId, getNewOrUpdatedCase]);

  const hasErrors = useMemo(() => {
    const caseDataFormHasErrors = !!Object.values(caseDataForm.validation).find(
      (validation) => !!validation.error
    );

    const caseAssignmentFormsHasErrors = !!Object.values(
      caseAssignmentForms.validations
    ).find((vs) => Object.values(vs).find((v) => !!v.error));

    return caseDataFormHasErrors || caseAssignmentFormsHasErrors;
  }, [caseAssignmentForms.validations, caseDataForm.validation]);

  const controlsContextValue = useMemo(
    (): Controls => ({
      save: handleSave,
      checkForOverlaps,
      checkForCommentsNeeded,
      checkIfConnectedTradeInWillAutoUpdate,
      startEdit: handleStartEdit,
      cancelEdit: handleCancelEdit,
      copy: handleCopy,
      editMode: controlsState.editMode,
      getNewOrUpdatedCase,
      hasErrors,
      createCaseStatus,
      // updateCaseStatus: convertToLegacyStatus(updateCaseCall.status),
      updateCaseStatus,
      sendCommentToFieldTester,
      isNewCase:
        !controlsState.caseBeingEdited ||
        controlsState.caseBeingEdited.case.caseID === -1,
      savedIsElectricCase:
        controlsState.caseBeingEdited?.case.isElectric === true,
    }),
    [
      handleSave,
      checkForOverlaps,
      checkForCommentsNeeded,
      checkIfConnectedTradeInWillAutoUpdate,
      handleStartEdit,
      handleCancelEdit,
      handleCopy,
      controlsState.editMode,
      getNewOrUpdatedCase,
      hasErrors,
      createCaseStatus,
      updateCaseStatus,
      sendCommentToFieldTester,
      controlsState.caseBeingEdited,
    ]
  );

  return (
    <Suspense fallback="Laddar...">
      <ControlsContext.Provider value={controlsContextValue}>
        <CaseDataFormContext.Provider value={caseDataForm}>
          <CaseAssignmentFormContext.Provider value={caseAssignmentForms}>
            {children}
          </CaseAssignmentFormContext.Provider>
        </CaseDataFormContext.Provider>
      </ControlsContext.Provider>
    </Suspense>
  );
};

export const useDetailedCaseDataForm = () => {
  const value = useContext(CaseDataFormContext);
  if (!value) {
    throw Error(
      "Can't useDetailedCaseDataForm() outside of DetailedCaseFormProvider"
    );
  }
  return value;
};

export const useDetailedCaseAssignmentForm = () => {
  const value = useContext(CaseAssignmentFormContext);
  if (!value) {
    throw Error(
      "Can't useDetailedCaseAssignmentForm() outside of DetailedCaseFormProvider"
    );
  }
  return value;
};

export const useDetailedCaseFormControl = () => {
  const value = useContext(ControlsContext);
  if (!value) {
    throw Error(
      "Can't useDetailedCaseFormControl() outside of DetailedCaseFormProvider"
    );
  }
  return value;
};

export default DetailedCaseFormProvider;
